import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Calculator,
  Calendar as CalendarIcon,
  Clock,
  AlertCircle,
  CheckCircle,
  Loader,
  User,
} from "lucide-react";

// lunar library (commonjs bundle)
import { Lunar, Solar } from '../../services/lunar.js';
import { validateDate, isAuthenticated } from "../../utils/auth";
import { analyzeDaymatchRules } from "./_helper_zeri.jsx";
import apiService from "../../services/api";

const WeekHeaders = ["一", "二", "三", "四", "五", "六", "日"];

const Zeri = ({ zecolor = 0 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showContactInfo, setShowContactInfo] = useState(false);

  const [goodtime, setGoodtime] = useState([]);
  const [badtime, setBadtime] = useState([]);
  const goodtime_number = goodtime.length;
  const badtime_number = badtime.length;

  const now = new Date();
  const curYear = now.getFullYear();
  const curMonth = now.getMonth() + 1;
  const curDay = now.getDate();

  // panel1 selections
  const [year, setYear] = useState(curYear);
  const [month, setMonth] = useState(curMonth);
  const [confirmedYear, setConfirmedYear] = useState(curYear);
  const [confirmedMonth, setConfirmedMonth] = useState(curMonth);

  // selected day in calendar
  const [selectedDay, setSelectedDay] = useState(curDay);

  // calendar matrix rows (array of weeks -> days)
  const [calendarRows, setCalendarRows] = useState([]);

  // bottom controls
  const [method, setMethod] = useState("奇门择日");
  const [timeRange, setTimeRange] = useState("0-12时");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // year options starting from current year (show +/- 10 years)
  const yearOptions = Array.from({ length: 21 }, (_, i) => curYear - 10 + i);

  useEffect(() => {
    generateCalendar(confirmedYear, confirmedMonth);
    // default select day if month matches current
    if (confirmedYear === curYear && confirmedMonth === curMonth) {
      setSelectedDay(curDay);
    } else {
      setSelectedDay(1);
    }
  }, [confirmedYear, confirmedMonth]);

  const generateCalendar = (y, m) => {
    const firstDay = new Date(y, m - 1, 1);
    // JS: 0=Sun .. 6=Sat. We want Monday start: map Sun->6, Mon->0 ...
    const startIndex = (firstDay.getDay() + 6) % 7;
    const daysInMonth = new Date(y, m, 0).getDate();

    const cells = [];
    // add leading blanks
    for (let i = 0; i < startIndex; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m - 1, d));
    // pad to multiple of 7
    while (cells.length % 7 !== 0) cells.push(null);

    const rows = [];
    for (let i = 0; i < cells.length; i += 7) {
      rows.push(cells.slice(i, i + 7));
    }

    // enrich with lunar/gan-zhi info
    const enrichedRows = rows.map((week) =>
      week.map((dt) => {
        if (!dt) return null;
        try {
          const lunar = Lunar.fromYmd(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
          return {
            date: dt,
            solarDay: dt.getDate(),
            lunarDayChinese: lunar.getDayInChinese(),
            lunarMonthChinese: lunar.getMonthInChinese(),
            lunarYearChinese: lunar.getYearInChinese(),
            yearGanZhi: lunar.getYearInGanZhi(),
            monthGanZhi: lunar.getMonthInGanZhi(),
          };
        } catch (err) {
          return {
            date: dt,
            solarDay: dt.getDate(),
            lunarDayChinese: "",
            lunarMonthChinese: "",
            lunarYearChinese: "",
            yearGanZhi: "",
            monthGanZhi: "",
          };
        }
      })
    );

    setCalendarRows(enrichedRows);
  };

  const handleProfessionalClick = async () => {
    if (!isAuthenticated()) {
      sessionStorage.setItem("post_login_return", JSON.stringify({
        path: location.pathname,
        search: location.search
      }));
      navigate("/login");
      return;
    }
    
    setLoading(true);
    try {
      const userData = await apiService.getCurrentUser();
      const hasSubscription = userData.subscriptions?.some(
        sub => sub.service_code === 'qimen_02' && sub.is_valid
      );

      if (hasSubscription) {
        // 已订阅，跳转到专业版页面
        navigate("/zeri/zeri_zhuanye");
      } else {
        // alert("您尚未订阅“奇门择日”专业版服务，请先订阅。");
        if (window.confirm("您尚未订阅“奇门择日”专业版服务，是否前往订阅？")) {
          navigate(`/purchase?code=qimen_02`);
        }
      }
    } catch (err) {
      console.error(err);
      alert("验证订阅状态失败，请重试");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    setConfirmedYear(year);
    setConfirmedMonth(month);
    const targetDay = (year === curYear && month === curMonth) ? curDay : 1;
    handleStart(targetDay, year, month);
  };

  const handleStart = async (dayInput, yearInput, monthInput) => {
    const currentDay = typeof dayInput === 'number' ? dayInput : selectedDay;
    const useYear = yearInput || confirmedYear;
    const useMonth = monthInput || confirmedMonth;

    setError("");
    // ensure a day is selected
    if (!currentDay) {
      setError("请先在日历中选择一个日期");
      return;
    }

    // validate date
    if (!validateDate(useYear, useMonth, currentDay)) {
      setError("选择的日期不合法");
      return;
    }

    // call backend zeri_caculate
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const payload = {
        year: useYear,
        month: useMonth,
        day: currentDay,
        method,
        time_range: timeRange,
      };

      const result = analyzeDaymatchRules(new Date(useYear, useMonth - 1, currentDay));

      const newGoodTime = [];
      const newBadTime = [];

      result.forEach(item => {
        if (item.good && item.good.length > 0) {
          item.good.forEach(r => {
            newGoodTime.push(`${item.time}: ${r.name}`);
          });
        }
        if (item.bad && item.bad.length > 0) {
          item.bad.forEach(r => {
            newBadTime.push(`${item.time}: ${r.name}`);
          });
        }
      });

      setGoodtime(newGoodTime);
      setBadtime(newBadTime);

      // 一下是通过后台的方法方法
      // const resp = await fetch("/api/zeri_caculate", {
      //   method: "POST",
      //   headers: {
      //     "Content-Type": "application/json",
      //     ...(token ? { Authorization: `Bearer ${token}` } : {}),
      //   },
      //   body: JSON.stringify(payload),
      // });

      // if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      // const data = await resp.json();

      // // navigate to detail page with result
      // navigate("/zeri/zeridetail", { state: { result: data } });
    } catch (err) {
      console.error(err);
      setError("计算失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleStart();
  }, []);

  // Global date object for selected day
  const sd = new Date(confirmedYear, confirmedMonth - 1, selectedDay);
  const sol = Solar.fromYmd(confirmedYear, confirmedMonth, selectedDay);
  const lun = sol.getLunar();
  // helper: display panel1 gan-zhi/lunar for confirmed month (use first day)
  let panelInfo = null;
  try {

    const lunar = Lunar.fromYmd(curYear, curMonth, curDay);
    panelInfo = {
      yearGanZhi: lunar.getYearInGanZhi(),
      monthGanZhi: lunar.getMonthInGanZhi(),
      lunarYearChinese: lunar.getYearInChinese(),
      lunarMonthChinese: lunar.getMonthInChinese(),
    };
  } catch (err) {
    panelInfo = null;
  }

  // selected day lunar/gan-zhi
  let selectedLunar = null;
  try {

    selectedLunar = {
      dayGanZhi:
        typeof lun.getDayInGanZhi === "function"
          ? lun.getDayInGanZhi()
          : typeof lun.getDayInGanZhiExact === "function"
            ? lun.getDayInGanZhiExact()
            : "",
      dayInChinese: lun.getDayInChinese(),
      lunarMonthChinese: lun.getMonthInChinese(),
    };
  } catch (err) {
    selectedLunar = null;
  }

  // C 区数据利用 lunar.js 获取多项信息
  let cData = {
    zhiXing: "—",
    yearGanZhi: '',
    yearShengXiao: '',
    yearNaYin: '',
    lunarYearInChinese: '',
    lunarMonthInChinese: '',
    lunarDayInChinese: '',
    yearZhiShui: '',
    yearGenTian: '',
    yearDeJin: '',
    yearFenBing: '',
    yearKongWang: '',
    monthGanZhi: '',
    monthNaYin: '',
    monthTaiShen: '',
    monthKongWang: '',
    dayGanZhi: '',
    dayShengXiao: '',
    dayNaYin: '',
    dayTaiShen: '',
    dayZhiShen: '',
    dayChong: '',
    dayTianShen: '',
    daySha: '',
    dayLu: '',
    dayJiShen: [],
    dayXiongSha: [],
    dayYi: [],
    dayJi: [],
    dayKongWang: '',
    dayJiuXing: '',
    prevJq: '',
    nextJq: '',
    liuYao: '',

    wuHou: '',
    weekInChinese: '',
    xingZuo: '',
    xiu: '',
    positionFu: '',
    positionCai: '',
    positionXi: '',
    positionYangGui: '',
    positionYinGui: ''


  };

  try {

    const lunar = sol.getLunar()
    cData.yearGanZhi = lunar.getYearInGanZhi()
    cData.yearShengXiao = lunar.getYearShengXiao()
    cData.yearNaYin = lunar.getYearNaYin()
    cData.lunarYearInChinese = lunar.getYearInChinese()
    cData.yearKongWang = lunar.getYearXunKong()
    cData.monthGanZhi = lunar.getMonthInGanZhi()
    cData.monthShengXiao = lunar.getMonthShengXiao()
    cData.monthNaYin = lunar.getMonthNaYin()
    cData.monthTaiShen = lunar.getMonthPositionTai()
    cData.lunarMonthInChinese = lunar.getMonthInChinese()
    cData.monthKongWang = lunar.getMonthXunKong()

    cData.yueXiang = lunar.getYueXiang()
    cData.wuHou = lunar.getHou() + ' ' + lunar.getWuHou()
    cData.yueMing = lunar.getSeason()

    cData.xingZuo = sol.getXingZuo();
    cData.xiu = lun.getXiu() + "(" + lun.getXiuLuck() + ")";

    cData.dayGanZhi = lunar.getDayInGanZhi()
    cData.dayShengXiao = lunar.getDayShengXiao()
    cData.dayNaYin = lunar.getDayNaYin()
    cData.dayTaiShen = lunar.getDayPositionTai()
    cData.pengZuGan = lunar.getPengZuGan()
    cData.pengZuZhi = lunar.getPengZuZhi()
    cData.dayZhiShen = lunar.getZhiXing()
    cData.dayTianShen = lunar.getDayTianShen() + '(' + lunar.getDayTianShenType() + '日)'
    cData.daySha = lunar.getDaySha()
    cData.liuYao = lunar.getLiuYao()
    cData.dayLu = lunar.getDayLu()
    cData.dayJiShen = lunar.getDayJiShen()
    cData.dayXiongSha = lunar.getDayXiongSha()
    cData.dayYi = lunar.getDayYi()
    cData.dayJi = lunar.getDayJi()
    cData.lunarDayInChinese = lunar.getDayInChinese()

    const dayNineStar = lunar.getDayNineStar()
    cData.dayJiuXing = dayNineStar.toString()

    const _prevJq = lunar.getPrevJieQi(true)
    const _prevJqSolar = _prevJq.getSolar()
    cData.prevJq = _prevJq.getName() + '：' + _prevJqSolar.toYmd() + ' '
    const _nextJq = lunar.getNextJieQi(true)
    const _nextJqSolar = _nextJq.getSolar()
    cData.nextJq = _nextJq.getName() + '：' + _nextJqSolar.toYmd() + ' '

    cData.dayChong = lunar.getDayShengXiao() + '日冲' + lunar.getDayChongDesc()
    cData.dayKongWang = lunar.getDayXunKong()

    cData.xiu = lunar.getGong() + '方' + lunar.getXiu() + lunar.getZheng() + lunar.getAnimal() + '(' + lunar.getXiuLuck() + ')'

    cData.positionXi = lunar.getPositionXiDesc()
    cData.positionFu = lunar.getPositionFuDesc()
    cData.positionCai = lunar.getPositionCaiDesc()
    cData.positionYangGui = lunar.getPositionYangGuiDesc()
    cData.positionYinGui = lunar.getPositionYinGuiDesc()

    // Region 1
    // Region 2
    // Region 3
    cData.zhiXing = lun.getZhiXing();
    cData.tianShen = lun.getDayTianShen();
    cData.dayYi = lun.getDayYi();
    cData.dayJi = lun.getDayJi();

    // Region 4
    cData.dayJiShen = lun.getDayJiShen();
    cData.dayXiongSha = lun.getDayXiongSha();
    cData.huangHei = lun.getDayTianShenType();



  } catch (e) {
    console.error(e);
  }

  return (
    <div className="bg-gray-50 py-4 sm:py-8">
      <main className="max-w-5xl mx-auto px-2 sm:px-4">
        <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 flex items-center gap-2">
          <CalendarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-600" />
          择日·择时
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 ">
          {/* Panel2 - Calendar */}
          <div className="bg-white rounded-lg shadow p-3 sm:p-6">
            <div className="flex items-center gap-2 w-full sm:w-auto  ">
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="flex-1 sm:flex-none px-2 py-1.5 border rounded-md text-sm"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}年
                  </option>
                ))}
              </select>
              <select
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value))}
                className="flex-1 sm:flex-none px-2 py-1.5 border rounded-md text-sm"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((mm) => (
                  <option key={mm} value={mm}>
                    {mm}月
                  </option>
                ))}
              </select>
              <button
                onClick={handleConfirm}
                className="px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm whitespace-nowrap"
              >
                确认
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full table-fixed border-collapse">
                <thead>
                  <tr>
                    {WeekHeaders.map((h) => (
                      <th key={h} className="py-2 text-xs sm:text-sm text-gray-600 border-b">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {calendarRows.map((week, wi) => (
                    <tr key={wi} className="align-top">
                      {week.map((cell, ci) => (
                        <td
                          key={ci}
                          className={`h-16 sm:h-24 align-top p-1 sm:p-2 border-r border-b text-xs sm:text-sm`}
                        >
                          {cell ? (
                            <div
                              onClick={() => {
                                const d = cell.date.getDate();
                                setSelectedDay(d);
                                handleStart(d);
                              }}
                              className={`h-full p-1 sm:p-2 rounded-md cursor-pointer flex flex-col justify-between transition-colors ${selectedDay === cell.date.getDate()
                                ? "bg-indigo-50 border border-indigo-200"
                                : "hover:bg-gray-50"
                                }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="text-xs sm:text-sm font-medium text-gray-800">{cell.solarDay}</div>
                                <div className="text-[10px] sm:text-xs text-gray-400 scale-90 origin-right sm:scale-100">{cell.lunarMonthChinese}</div>
                              </div>
                              <div className="text-[10px] sm:text-xs text-gray-500 mt-1">{cell.lunarDayChinese}</div>
                            </div>
                          ) : (
                            <div className="h-full p-1 sm:p-2" />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* bottom controls */}
            <div className="mt-4 flex flex-col sm:flex-row items-center gap-3">
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <label className="text-sm text-gray-600 whitespace-nowrap">方法</label>
                <select value={method} onChange={(e) => setMethod(e.target.value)} className="flex-1 sm:flex-none px-3 py-2 border rounded-md text-sm">
                  <option>奇门择日</option>
                  <option>干支择日</option>
                  <option>六壬择日</option>
                  <option>太乙择日</option>
                </select>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <label className="text-sm text-gray-600 whitespace-nowrap">吉时</label>
                <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} className="flex-1 sm:flex-none px-3 py-2 border rounded-md text-sm">
                  <option>0-24时</option>
                  <option>0-12时</option>
                  <option>13-24时</option>
                </select>
              </div>

              <div className="w-full sm:w-auto sm:ml-auto">
                <button
                  onClick={handleProfessionalClick}
                  disabled={loading}
                  className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-60 flex items-center justify-center gap-2 text-sm"
                >
                  {loading ? <Loader className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
                  专业择日
                </button>
              </div>
            </div>

            {error && (
              <div className="mt-3 text-sm text-red-600 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" /> {error}
              </div>
            )}
          </div>
          {/* Panel1 */}
          <div className="bg-white rounded-lg shadow p-3 sm:p-6">
            <div className="mt-2 border-t pt-4">
              {/* 弹性容器，移动端自然高度，桌面端可固定高度 */}
              <div className="flex flex-col gap-4 lg:h-[600px]">

                {/* 上半部分: 日期大字 + 吉凶列表 */}
                <div className="flex flex-col sm:flex-row gap-4 lg:basis-[30%] shrink-0">
                  {/* A部分: 日期显示 */}
                  <div className="flex flex-row sm:flex-col gap-2 sm:gap-0 sm:basis-[40%]">
                    {/* A 上：日期标题 */}
                    <div className="flex-1 sm:flex-none flex flex-col items-center justify-center rounded-md p-2 sm:py-3 bg-green-600 text-white shadow-sm">
                      <div className="text-xs sm:text-sm opacity-90 font-light tracking-wide">
                        {confirmedYear}年{confirmedMonth}月
                      </div>
                      <div className="text-sm sm:text-lg font-bold mt-0.5">
                        {selectedDay}日 <span className="text-xs sm:text-sm font-normal opacity-90 ml-1">周{["日", "一", "二", "三", "四", "五", "六"][sd.getDay()]}</span>
                      </div>
                    </div>


                    {/* A 下：干支 & 农历 */}
                    <div className="hidden sm:flex mt-3 flex-col items-center justify-center space-y-1">
                      <div className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium text-gray-800 tracking-widest">
                        {lun.getYearInGanZhi()}年{lun.getMonthInGanZhi()}月{cData.dayGanZhi}日
                      </div>
                      <div className="text-xs text-gray-500 font-medium">
                        农历 {lun.getMonthInChinese()}月{lun.getDayInChinese()}
                      </div>
                    </div>
                  </div>

                  {/* 移动端补充显示干支 (A 下 移动端版本) */}
                  <div className="sm:hidden flex items-center justify-between bg-gray-50 rounded-md px-3 py-2 mt-2 border border-gray-100">
                    <div className="text-sm font-medium text-gray-700 tracking-wide">
                      {lun.getYearInGanZhi()} {lun.getMonthInGanZhi()} {cData.dayGanZhi}
                    </div>
                    <div className="text-xs text-gray-500">
                      农历 {lun.getMonthInChinese()}月{lun.getDayInChinese()}
                    </div>
                  </div>

                  {/* B部分: 吉凶列表 */}
                  <div className="flex flex-col sm:basis-[60%] h-48 sm:h-auto bg-white border border-gray-200 rounded-md overflow-y-auto p-2 shadow-inner">
                    {/* 吉时 */}
                    {goodtime_number > 0 && Array.isArray(goodtime) && goodtime.slice(0, goodtime_number).map((g, idx) => (
                      <div key={`g-${idx}`} className="py-1 text-xs sm:text-sm text-green-600 border-b border-gray-100 last:border-0">
                        <span className="font-bold">吉：</span>
                        <span>{g}</span>
                      </div>
                    ))}

                    {/* 凶时 */}
                    {badtime_number > 0 && Array.isArray(badtime) && badtime.slice(0, badtime_number).map((b, idx) => (
                      <div key={`b-${idx}`} className="py-1 text-xs sm:text-sm text-yellow-600 border-b border-gray-100 last:border-0">
                        <span className="font-bold">凶：</span>
                        <span>{b}</span>
                      </div>
                    ))}

                    {(!goodtime_number && !badtime_number) && (
                      <div className="text-xs text-gray-400 text-center mt-4">暂无吉凶信息</div>
                    )}
                  </div>
                </div>

                {/* 下半部分: 详细信息 C 部分 */}
                <div className="lg:basis-[70%] overflow-y-auto border-t border-gray-100 pt-2 lg:h-full">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    {/* Region 1: Basic Info */}
                    <div className="space-y-1 bg-gray-50 p-2 rounded">
                      <div className="font-bold text-indigo-600 mb-1 border-b border-indigo-100 pb-1">基本信息</div>
                      <div className="flex justify-between"><span className="text-gray-500">星座:</span> <span>{cData.xingZuo}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">二十八星宿:</span> <span>{cData.xiu}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">上一节气:</span> <span className="text-right">{cData.prevJq}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">下一节气:</span> <span className="text-right">{cData.nextJq}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">日纳音:</span> <span>{cData.dayNaYin}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">日旬空:</span> <span>{cData.dayKongWang}</span></div>
                    </div>

                    {/* Region 2: Gods */}
                    <div className="space-y-1 col-span-2 sm:col-span-1 bg-gray-50 p-2 rounded">
                      <div className="font-bold text-indigo-600 mb-1 border-b border-indigo-100 pb-1">神煞方位</div>
                      <div className="flex justify-between"><span className="text-gray-500">喜神:</span> <span>{cData.positionXi}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">福神:</span> <span>{cData.positionFu}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">财神:</span> <span>{cData.positionCai}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">阳贵:</span> <span>{cData.positionYangGui}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">阴贵:</span> <span>{cData.positionYinGui}</span></div>
                      <div className="flex justify-between mt-1 pt-1 border-t border-gray-200"><span className="text-gray-500">冲煞:</span> <span className="text-red-500">{cData.dayChong} {cData.sha}</span></div>
                    </div>

                    {/* Region 3: JianChu / YiJi */}
                    <div className="space-y-1 col-span-2 sm:col-span-1 bg-gray-50 p-2 rounded">
                      <div className="font-bold text-indigo-600 mb-1 border-b border-indigo-100 pb-1">宜 · 忌</div>
                      <div className="flex gap-2 mb-2">
                        <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">十二神</span>
                        <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded text-xs">{cData.dayTianShen}</span>
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="flex items-start gap-2">
                          <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs shrink-0 mt-0.5">宜</span>
                          <span className="text-gray-700 leading-snug">{cData.dayYi.join(' ')}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded text-xs shrink-0 mt-0.5">忌</span>
                          <span className="text-gray-700 leading-snug">{cData.dayJi.join(' ')}</span>
                        </div>
                      </div>
                    </div>

                    {/* Region 4: Other Stars */}
                    <div className="space-y-1 col-span-2 sm:col-span-1 bg-gray-50 p-2 rounded">
                      <div className="font-bold text-indigo-600 mb-1 border-b border-indigo-100 pb-1">吉神凶煞</div>
                      <div className="mb-2">
                        <span className="bg-green-50 text-green-800 px-2 py-1 rounded text-xs mb-1 inline-block">吉神</span>
                        <span className="text-gray-700 leading-snug text-xs">{cData.dayJiShen.join(' ')}</span>
                      </div>
                      <div>
                        <span className="bg-red-50 text-red-800 px-2 py-1 rounded text-xs mb-1 inline-block">凶煞</span>
                        <span className="text-gray-700 leading-snug text-xs">{cData.dayXiongSha.join(' ')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>


        </div>
        <div className="h-6"></div>
        {showContactInfo && (
          <div className="mb-6 bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-center">
            <p className="text-lg font-medium text-indigo-800">请扫描二维码联系「圆運」，由专业人士为您服务</p>
          </div>
        )}

        {/* 使用说明 */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          <p className="font-semibold mb-2">使用说明</p>
          <ol className="list-decimal list-inside space-y-1">
            <li>最常规的择时方法，吉：天显时格、玉女守门等；凶：五不遇时、天克地冲、阴差阳错等（规则库会不断完善）。</li>
            <li>择时：是一个非常复杂的推演，需要根据求占人的八字和所问事情进行分析多。</li>
            <li>择时方法有很多：奇门择时、六壬择时、太乙择时等等，如需要可以联系专业命理师服务。</li>
          </ol>
        </div>
      </main>

    </div>
  );
};

export default Zeri;